import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  LinearProgress,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  makeStyles,
  Select,
  MenuItem,
  Button
} from '@material-ui/core';
import { Theme } from '@mui/material';
import clsx from 'clsx';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getMenuItems, formatTableData, dataFromTableData } from './utils';
import { SearchAndSort, SortOrder } from '..';
import { GlobalGuide } from '../../models';
import { GlobalGuideReport } from '../../redux/globalGuides';
import { filterData, sortData } from '../../services/filtering';
import { TableCellSort } from '../TableCellSort';

export interface GlobalGuideSearchTableProps {
  data: GlobalGuide[] | null;
  selectedItem?: GlobalGuide;
  onClick: (data?: GlobalGuide) => void;
  getGlobalGuideReportDetails: (reportId: string, versionId: string) => void;
  generateGlobalGuideReport: (reportId: string) => void;
  report: GlobalGuideReport | null;
  status: number | null;
}

export interface MakeStylesProps {
  longestOptionLength: number;
}

const useStyles = makeStyles<Theme, MakeStylesProps>((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 0),
    height: '100%',
    background: theme.palette.background.default
  },
  title: {
    color: theme.palette.text.primary,
    padding: theme.spacing(2, 2, 0)
  },
  menuWrapper: {
    padding: theme.spacing(1, 2)
  },
  table: {
    flexGrow: 1,
    padding: theme.spacing(0),
    margin: theme.spacing(0),
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.5, 2),
      cursor: 'pointer'
    }
  },
  select: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderStyle: 'none'
      },
      '&.Mui-focused fieldset': {
        borderStyle: 'none'
      }
    },
    '& .MuiSelect-root': {
      minWidth: theme.spacing(16)
    }
  },
  transactionSelect: {
    width: (props) => `${props.longestOptionLength}ch`, // Using ch to adjust the width based of the number of characters
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderStyle: 'none'
      }
    }
  },
  reportButtonsContainer: {
    display: 'inline-flex',
    justifyContent: 'flex-end'
  },
  reportButton: {
    marginRight: theme.spacing(2)
  },
  buttonAnimation: {
    animation: `$myEffect 3000ms infinite ease-in-out`
  },
  '@keyframes myEffect': {
    '0%': {
      color: 'rgba(60, 42, 142, 1.0)'
    },
    '50%': {
      color: 'rgba(60, 42, 142, 0.3)'
    },
    '100%': {
      color: 'rgba(60, 42, 142, 1.0)'
    }
  }
}));

export const GlobalGuideSearchTable = ({
  data,
  selectedItem,
  report,
  status,
  onClick,
  getGlobalGuideReportDetails,
  generateGlobalGuideReport
}: GlobalGuideSearchTableProps) => {
  const { t } = useTranslation();
  const flags = useFlags();

  const [reviewType, setReviewType] = useState<string>(t('globalguide:wtp-review-type-audit-risk'));
  const [longestReviewTypeOption, setLongestReviewTypeOption] = useState<string>(''); // Keeps the longest option
  const formattedTableData = formatTableData(data, reviewType);

  const [filterObject, setFilter] = useState({});
  const [sortObject, setSort] = useState({});

  const menuItems = getMenuItems(data, t);

  const reportGeneratingStatus = status === 1;

  const showButtons = !flags.showGlobalGuideReportButtons;

  const getLongestMenuItem = (menuItems: Array<{ text: string; value: string }>): string => {
    let length = 0;
    let longestOption = '';
    menuItems.forEach((menuItem) => {
      if (menuItem.value.length > length) {
        length = menuItem.value.length;
        longestOption = menuItem.value;
      }
    });
    return longestOption;
  };

  useEffect(() => {
    if (menuItems) {
      const longestOption = getLongestMenuItem(menuItems);
      setLongestReviewTypeOption(longestOption);
    }
  }, [menuItems]);

  const classes = useStyles({ longestOptionLength: longestReviewTypeOption?.length });

  const searchHandler = (filterBy: string, filterString: string) => {
    setFilter({ ...filterObject, [filterBy]: filterString });
  };

  const sortHandler = (sortBy: string, sortOrder: string) => {
    setSort({ sortBy, sortOrder });
  };

  const countryKey = 'country';
  const localeCountry = t(`globalguide:column-${countryKey}`);

  const filteredAndSortedGlobalGuideData = sortData(
    filterData(formattedTableData, filterObject),
    sortObject as { sortBy: string; sortOrder: SortOrder }
  );

  const handleMenuChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReviewType(event.target?.value);
  };

  const selectClasses = clsx({
    [classes.transactionSelect]: true
  });

  return (
    <Paper className={classes.root}>
      <Box className={classes.reportButtonsContainer}>
        {showButtons && (
          <>
            <Button
              variant="contained"
              color="secondary"
              disabled={reportGeneratingStatus}
              className={`${classes.reportButton} ${reportGeneratingStatus ? classes.buttonAnimation : ''}`}
              onClick={() => {
                generateGlobalGuideReport(report?.reportId ?? '');
              }}
            >
              {reportGeneratingStatus
                ? t('globalguide:global_guide_generating_report')
                : t('globalguide:global_guide_generate_report')}
            </Button>

            <Button
              variant="contained"
              color="secondary"
              disabled={reportGeneratingStatus}
              className={classes.reportButton}
              onClick={() => {
                getGlobalGuideReportDetails(report?.reportId ?? '', report?.versions[0]?.versionId ?? '');
              }}
            >
              {t('globalguide:global_guide_download_report')}
            </Button>
          </>
        )}
      </Box>
      <Typography variant="h2" className={classes.title}>
        {t('globalguide:title')}
      </Typography>
      {Array.isArray(data) ? (
        <>
          <Box className={classes.menuWrapper}>
            <Select
              disableUnderline
              className={selectClasses}
              variant="outlined"
              value={reviewType}
              onChange={(e: any) => {
                handleMenuChange(e);
              }}
            >
              {menuItems.map((item) => (
                <MenuItem key={item.text} value={item.value}>
                  {item.text}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <TableContainer className={classes.table} component={Box}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 260 }}>
                    <SearchAndSort
                      field={localeCountry}
                      onSearchChange={(filterBy: string) => {
                        searchHandler(countryKey, filterBy);
                      }}
                      onSortClicked={(sortOrder: 'asc' | 'desc') => {
                        sortHandler(countryKey, sortOrder);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TableCellSort
                      onSortClicked={(sortOrder: 'asc' | 'desc') => {
                        sortHandler('value', sortOrder);
                      }}
                    >
                      {t('globalguide:column-value')}
                    </TableCellSort>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedGlobalGuideData.map((row) => (
                  <TableRow
                    key={row.id}
                    role="row"
                    tabIndex={-1}
                    selected={selectedItem?.countryId === row.id}
                    onClick={() => {
                      onClick(dataFromTableData(data, row));
                    }}
                  >
                    <TableCell>{row[countryKey]}</TableCell>
                    <TableCell>{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <LinearProgress />
      )}
    </Paper>
  );
};
